import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import LoadingButton from "@mui/lab/LoadingButton";
import LockIcon from "@mui/icons-material/Lock";

const formatMoneda = (valor) =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(valor ?? 0);

export default function OrtodonciaTabla({
  reportData,
  onExportar,
  onCerrarLiquidacion,
  exportando,
  cerrando,
}) {
  if (!reportData) return null;

  const listado = reportData?.listado ?? [];
  const pagosSinPrestacion = reportData?.pagos_sin_prestacion ?? [];
  const prestacionesSinPago = reportData?.prestaciones_sin_pago ?? [];

  if (listado.length === 0) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 4,
          borderRadius: 2,
          border: "1px dashed",
          borderColor: "grey.300",
          backgroundColor: "grey.50",
          textAlign: "center",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          color="text.secondary"
        >
          Sin liquidaciones cerradas
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
          No se encontraron liquidaciones cerradas para el período y clínica
          seleccionados.
        </Typography>
      </Box>
    );
  }

  const totalesPorOdontologo = listado.reduce((acc, row) => {
    if (!acc[row.odontologo]) {
      acc[row.odontologo] = {
        pago_caja: 0,
        pago_25: 0,
        gasto_laboratorio: 0,
        honorarios: 0,
      };
    }
    acc[row.odontologo].pago_caja += row.pago_caja ?? 0;
    acc[row.odontologo].pago_25 += row.pago_25 ?? 0;
    acc[row.odontologo].gasto_laboratorio += row.gasto_laboratorio ?? 0;
    acc[row.odontologo].honorarios += row.honorarios ?? 0;
    return acc;
  }, {});

  const totalGeneral = listado.reduce(
    (acc, row) => {
      acc.pago_caja += row.pago_caja ?? 0;
      acc.pago_25 += row.pago_25 ?? 0;
      acc.gasto_laboratorio += row.gasto_laboratorio ?? 0;
      acc.honorarios += row.honorarios ?? 0;
      return acc;
    },
    { pago_caja: 0, pago_25: 0, gasto_laboratorio: 0, honorarios: 0 },
  );

  return (
    <Box sx={{ marginTop: 3 }}>
      {/* Botón exportar */}
      {/* Barra de acciones */}
      {(onExportar || onCerrarLiquidacion) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            marginBottom: 2,
          }}
        >
          {/* Alertas a la izquierda */}
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}
          >
            {prestacionesSinPago.length > 0 && (
              <Alert severity="warning" sx={{ py: 0 }}>
                {prestacionesSinPago.length} prestación/es sin pago asociado.
              </Alert>
            )}
            {pagosSinPrestacion.length > 0 && (
              <Alert severity="info" sx={{ py: 0 }}>
                {pagosSinPrestacion.length} pago/s sin prestación asociada.
              </Alert>
            )}
          </Box>

          {/* Botones a la derecha */}
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {onExportar && (
              <LoadingButton
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={onExportar}
                loading={exportando}
                loadingPosition="start"
                sx={{
                  backgroundColor: "#1D6F42",
                  ":hover": { bgcolor: "#155534" },
                }}
              >
                Exportar a Excel
              </LoadingButton>
            )}
            {onCerrarLiquidacion && (
              <LoadingButton
                size="medium"
                onClick={onCerrarLiquidacion}
                loading={cerrando}
                loadingPosition="start"
                endIcon={<LockIcon />}
                variant="contained"
                sx={{
                  backgroundColor: "primary.main",
                  ":hover": { bgcolor: "#475569", color: "white" },
                }}
              >
                Cerrar liquidación
              </LoadingButton>
            )}
          </Box>
        </Box>
      )}

      {/* Tabla principal */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.100" }}>
              <TableCell>
                <strong>Odontólogo</strong>
              </TableCell>
              <TableCell>
                <strong>Documento</strong>
              </TableCell>
              <TableCell>
                <strong>Paciente</strong>
              </TableCell>
              <TableCell>
                <strong>Prepaga</strong>
              </TableCell>
              <TableCell>
                <strong>Fecha</strong>
              </TableCell>
              <TableCell>
                <strong>Nomenclador</strong>
              </TableCell>
              <TableCell>
                <strong>Cód. Presupuesto</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Pago Caja</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Pago 25</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Gasto Lab.</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Honorarios</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listado.map((row, index) => {
              const esNuevoOdontologo =
                index === 0 || listado[index - 1].odontologo !== row.odontologo;
              const esUltimoDeOdontologo =
                index === listado.length - 1 ||
                listado[index + 1].odontologo !== row.odontologo;

              return (
                <React.Fragment key={index}>
                  {esNuevoOdontologo && (
                    <TableRow sx={{ backgroundColor: "primary.light" }}>
                      <TableCell colSpan={11} sx={{ py: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "primary.contrastText",
                            fontWeight: "bold",
                          }}
                        >
                          {row.odontologo}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow
                    hover
                    sx={{
                      backgroundColor: row.sin_pago ? "#fff8e1" : "inherit",
                    }}
                  >
                    <TableCell>{row.odontologo}</TableCell>
                    <TableCell>{row.paciente_documento}</TableCell>
                    <TableCell>{row.paciente_nombre}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.prepaga_codigo}
                        title={row.prepaga_razon}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{row.fecha}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {row.nomenclador_codigo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {row.codigos_presupuesto}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatMoneda(row.pago_caja)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoneda(row.pago_25)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoneda(row.gasto_laboratorio)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMoneda(row.honorarios)}
                      {row.cubierto && (
                        <Chip
                          label="C"
                          size="small"
                          color="success"
                          sx={{ ml: 0.5, height: 16, fontSize: 10 }}
                        />
                      )}
                    </TableCell>
                  </TableRow>

                  {esUltimoDeOdontologo && (
                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                      <TableCell colSpan={7} align="right">
                        <Typography variant="caption" fontWeight="bold">
                          Subtotal {row.odontologo}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatMoneda(
                            totalesPorOdontologo[row.odontologo]?.pago_caja,
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatMoneda(
                            totalesPorOdontologo[row.odontologo]?.pago_25,
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatMoneda(
                            totalesPorOdontologo[row.odontologo]
                              ?.gasto_laboratorio,
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {formatMoneda(
                            totalesPorOdontologo[row.odontologo]?.honorarios,
                          )}
                        </strong>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}

            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell colSpan={7} align="right">
                <Typography
                  variant="subtitle2"
                  sx={{ color: "primary.contrastText", fontWeight: "bold" }}
                >
                  TOTAL GENERAL
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="subtitle2"
                  sx={{ color: "primary.contrastText", fontWeight: "bold" }}
                >
                  {formatMoneda(totalGeneral.pago_caja)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="subtitle2"
                  sx={{ color: "primary.contrastText", fontWeight: "bold" }}
                >
                  {formatMoneda(totalGeneral.pago_25)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="subtitle2"
                  sx={{ color: "primary.contrastText", fontWeight: "bold" }}
                >
                  {formatMoneda(totalGeneral.gasto_laboratorio)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="subtitle2"
                  sx={{ color: "primary.contrastText", fontWeight: "bold" }}
                >
                  {formatMoneda(totalGeneral.honorarios)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagos sin prestación */}
      {pagosSinPrestacion.length > 0 && (
        <Box sx={{ marginTop: 3 }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ marginBottom: 1 }}
          >
            Pagos sin prestación asociada
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Odontólogo</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Documento</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Paciente</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Fuente</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Fecha/s</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Importe</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagosSinPrestacion.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{row.odontologo}</TableCell>
                    <TableCell>{row.paciente_documento}</TableCell>
                    <TableCell>{row.paciente_nombre}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.fuente}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{row.fechas?.join(", ")}</TableCell>
                    <TableCell align="right">
                      {formatMoneda(row.importe)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
